import { useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useReducer,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Node, ReactFlowProvider, useReactFlow } from 'reactflow';

import {
  artifactsDocumentToTapeBuilderGraph,
  tapeBuilderGraphToArtifactsDocument,
} from '@finley-technologies/tape-builder';

import { GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS } from '../requests/tapeBuilder.gql';
import { QAProvider } from './calcAgentQA/QAContext';
import { TapeBuilderDiffViewer } from './DiffViewer';
import {
  addIdsToEdges,
  getDefaultGraphState,
  GraphBasicState,
  GraphProvider,
  graphReducer,
  GraphState,
  isTapeBuilderMode,
  OrgSlugToOrgMapType,
  StoredGraphState,
  TapeBuilderMode,
} from './GraphContext';
import { TapeBuilderDiffDocument } from './TapeBuilderDiffDocument';
import { TapeBuilderStatus } from './TapeBuilderStatus';
import { defaultToastOptions } from './utils';
import { GraphPositions } from 'packages/common-frontend/components/ReactFlow';
import { repositionGraph } from 'packages/common-frontend/components/ReactFlow/layout/d3hierarchy';
import {
  useGetPipelineInstancesLazyQueryGQLType,
  useGetBorrowingBasePipelineStatusLazyQueryGQLType,
  useDeleteBorrowingBasePipelineArtifactDocumentWipMutation,
  useSaveBorrowingBasePipelineArtifactDocumentWipMutation,
  useSetNodePositionsMutation,
  useGetGraphWithPositionsLazyQueryGQLType,
} from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';
import {
  toServerPositions,
  fromServerPositions,
} from 'packages/tapeBuilder/ui/normalize';
import { TapeBuilderControl } from 'packages/tapeBuilder/ui/TapeBuilderControl';

import './TapeBuilder.scss';

type Props = {
  pipelineDefinitionId: string;
  orgSlugMap: OrgSlugToOrgMapType;
  adminMode: boolean;
};
const InternalComponent = ({
  pipelineDefinitionId, // @implementation: use this as primary query param.
  orgSlugMap,
  adminMode,
}: Props) => {
  const reactFlowInstance = useReactFlow();
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed mode from the URL so a link like `?mode=Monitor&reportingDate=...`
  // deep-links straight into monitor mode. Defaults to Edit when absent/invalid.
  const urlMode = searchParams.get('mode');
  const [mode, setMode] = useState<TapeBuilderMode>(
    isTapeBuilderMode(urlMode) ? urlMode : 'Edit',
  );
  const [diff, setDiff] = useState<TapeBuilderDiffDocument | null>(null);
  const getDefaultState = useCallback(
    () => getDefaultGraphState({ mode, adminMode }),
    [mode, adminMode],
  );

  const [graph, dispatch] = useReducer(graphReducer, getDefaultState());

  const updateFlowNodes = useCallback(
    ({ nodes, edges }: GraphBasicState, positions: GraphPositions) => {
      const needsRepositioning = nodes.some(
        (node) => positions[node.metadata.tapeBuilderNodeId] === undefined,
      );
      const flowNodes = needsRepositioning
        ? repositionGraph({
            nodes: nodes.map<Omit<Node, 'position'>>((node) => ({
              id: node.metadata.tapeBuilderNodeId,
              type: node.metadata.tapeBuilderNodeType,
              data: node.metadata.tapeBuilderNodeId,
            })),
            edges,
            xSpacing: 300,
            ySpacing: 300,
            disconnectedSpacing: 400,
          })
        : nodes.map<Node>((node) => ({
            id: node.metadata.tapeBuilderNodeId,
            type: node.metadata.tapeBuilderNodeType,
            data: node.metadata.tapeBuilderNodeId,
            position: positions[node.metadata.tapeBuilderNodeId],
          }));

      reactFlowInstance.setNodes(flowNodes);
      reactFlowInstance.setEdges(edges);
      reactFlowInstance.fitView();
    },
    [reactFlowInstance],
  );

  // Merge a partial update into the graph state. Only pass the fields you are
  // changing — the reducer preserves everything else from the latest state, so
  // concurrent async loads can't clobber each other's writes.
  const mergeGraph = useCallback((partial: Partial<GraphState>) => {
    dispatch({ type: 'mergeGraph', graph: partial });
  }, []);

  const clearGraph = () => {
    dispatch({ type: 'clearGraph' });
  };

  // artifact document to be displayed in the graph. (This could be different from the latestArtifactDocumentId)
  const [currentArtifactDocumentId, setCurrentArtifactDocumentId] = useState<
    string | null
  >(null);
  const toast = useToast(defaultToastOptions);

  const onError = useCallback(
    (error: any, prefix: string) => {
      // eslint-disable-next-line no-console
      console.error(prefix, error);
      const message = `${error}`;
      toast({
        id: `${Date.now()}`,
        title: message,
        status: 'error',
        duration: null, // errors are important, do not auto-close
      });
    },
    [toast],
  );

  // Document loads can be requested concurrently (status, mode change, run
  // selection). Only the most-recently-requested document may apply — otherwise
  // a slow earlier response (e.g. the WIP doc) can overwrite the nodes of a
  // newer one (e.g. the monitored run's doc) and leave a run's instances mapped
  // against the wrong graph. We record the last requested id in a ref and drop
  // any response that a newer request has superseded. A ref (not state) is used
  // deliberately: it must not feed callback identities, or the loader effect
  // below would re-fire every render.
  const requestedArtifactDocumentIdRef = useRef<string | null>(null);

  const [
    queryGraphWithPositions,
    { loading: loadingCurrentArtifactDocument },
  ] = useGetGraphWithPositionsLazyQueryGQLType({
    onError: (err) => {
      onError(err, 'queryGraphWithPositions.onError');
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      try {
        // Ignore a response superseded by a newer document request.
        if (
          data.borrowingBasePipelineArtifactDocumentInfo.artifactDocumentId !==
          requestedArtifactDocumentIdRef.current
        ) {
          return;
        }

        const {
          graph: { nodes, edges },
          artifactDocumentId,
          dbView,
        } = artifactsDocumentToTapeBuilderGraph({
          artifactsDocument:
            data.borrowingBasePipelineArtifactDocumentInfo
              .artifactsDocumentString,
        });

        // mode needs to be consistent with dbView.
        let newMode = mode;
        if ((mode === 'Monitor') !== dbView) {
          newMode = dbView ? 'Monitor' : 'Edit';
        }

        const edgesWithIDs = addIdsToEdges(edges);

        // Deliberately does NOT touch artifactInstances — a concurrent instance
        // load owns that field, and mergeGraph preserves what isn't passed here.
        mergeGraph({
          nodes,
          edges: edgesWithIDs,
          artifactDocumentId,
          dirty: false,
          mode: newMode,
        });

        const positions = fromServerPositions({
          dbPositions: data.nodePositions,
          nodes,
          edges,
        });

        updateFlowNodes({ nodes, edges: edgesWithIDs }, positions);
      } catch (error) {
        onError(error, 'queryGraphWithPositions.error.onCompleted');
      }
    },
  });

  const queryArtifactDocument = useCallback(
    (variables: { artifactDocumentId: string; dbView: boolean }) => {
      requestedArtifactDocumentIdRef.current = variables.artifactDocumentId;
      queryGraphWithPositions({
        variables: {
          ...variables,
          purpose: variables.dbView ? 'dataPipeline' : 'tapeBuilderEdit',
        },
      });
    },
    [queryGraphWithPositions],
  );

  const [
    setNodePositionsMutation,
    { loading: savingNodePositions },
  ] = useSetNodePositionsMutation({
    onError: (err) => onError(err, 'setNodePositionsMutation.onError'),
    onCompleted: () => {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: 'Graph Layout saved!',
        status: 'success',
        duration: 2000,
      });
    },
  });

  const saveNodePositions = useCallback(() => {
    // collect positions from react-flow.
    const flowPositions = reactFlowInstance
      .getNodes()
      .reduce<GraphPositions>((acc, node) => {
        // eslint-disable-next-line no-param-reassign
        acc[node.id] = node.position;
        return acc;
      }, {});

    const serverPosition = toServerPositions({
      positions: flowPositions,
      nodes: graph.nodes,
      edges: graph.edges,
    });

    // save the positions.
    setNodePositionsMutation({
      variables: {
        artifactDocumentId: graph.artifactDocumentId,
        positions: serverPosition,
        purpose: mode === 'Edit' ? 'tapeBuilderEdit' : 'dataPipeline',
      },
    });
  }, [
    reactFlowInstance,
    graph.nodes,
    graph.edges,
    graph.artifactDocumentId,
    setNodePositionsMutation,
    mode,
  ]);

  const [
    queryArtifactInstances,
    { loading: loadingArtifactInstances },
  ] = useGetPipelineInstancesLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data && data.getArtifactInstancesForPipelineRun) {
        const {
          artifactInstances,
          artifactDocumentId,
        } = data.getArtifactInstancesForPipelineRun;

        if (artifactDocumentId && mode === 'Monitor') {
          // Point the single document-loader at the run's committed doc. The
          // effect below loads it whenever currentArtifactDocumentId changes,
          // so we don't fire a competing load here.
          if (currentArtifactDocumentId !== artifactDocumentId) {
            setCurrentArtifactDocumentId(artifactDocumentId);
          }

          // see if we got any changes in status.
          const oldIdAndStatuses = graph.artifactInstances
            ? graph.artifactInstances
                .map(({ id, status }: { id: string; status: string }) => ({
                  id,
                  status,
                }))
                .sort((a: { id: string }, b: { id: string }) =>
                  a.id.localeCompare(b.id),
                )
            : [];
          const newIdAndStatuses = artifactInstances
            .map(({ id, status }: { id: string; status: string }) => ({
              id,
              status,
            }))
            .sort((a: { id: string }, b: { id: string }) =>
              a.id.localeCompare(b.id),
            );

          // update the state only if we got any new instances or statuses.
          // updating graph state here loses the selectedNode
          // state. we want to avoid that when there are no changes.
          if (!_.isEqual(oldIdAndStatuses, newIdAndStatuses)) {
            mergeGraph({ artifactInstances });
          }
        } else if (graph.artifactInstances !== null) {
          mergeGraph({ artifactInstances: null });
        }
      }
    },
    onError: (error) => {
      onError(error, 'queryArtifactInstances.onError');
    },
  });

  const [
    queryBorrowingBasePipelineStatus,
    { data: status, loading: loadingStatus },
  ] = useGetBorrowingBasePipelineStatusLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const creditFacilities = data.implementation.creditFacilities.map(
        ({ id, slug }) => ({
          id,
          slug,
        }),
      );
      const loanProducts = data.implementation.loanProducts.map(
        ({ id, slug }) => ({
          id,
          slug,
        }),
      );
      if (data.borrowingBasePipelineStatus.creditAgreementsNeedLink) {
        toast({
          status: 'warning',
          position: 'top',
          title: `Warning: Credit Agreements are not linked to latest committed tape-builder graph.  Please resolve any CA conflicts and re-commit CAs.`,
        });
      }
      mergeGraph({
        // @implementation: we should get rid of this explicit reference to the borrower org
        orgSlug: data.implementation.borrowerOrganization.slug,
        creditFacilities,
        loanProducts,
      });

      // Point at the WIP doc for the editable modes (Monitor's document is the
      // selected run's committed doc, set by the artifact-instance flow).
      if (mode !== 'Monitor') {
        const wipId = data.borrowingBasePipelineStatus.artifactDocumentWipId;
        setCurrentArtifactDocumentId(wipId);
        // Reload explicitly. A status refetch (after save / undo / refresh)
        // usually points at the same WIP id, so the load-on-change effect
        // below would NOT re-run — without this, the reloaded (clean) graph
        // never replaces the edited one and "Unsaved" would never clear.
        // The request-id guard makes the possible double-load harmless.
        queryArtifactDocument({
          artifactDocumentId: wipId,
          dbView: mode === 'View',
        });
      }
    },
    onError: (error) => {
      onError(error, 'queryBorrowingBasePipelineStatus.onError');
    },
  });

  const getStatus = useCallback(() => {
    queryBorrowingBasePipelineStatus({
      variables: { pipelineDefinitionId },
    });
  }, [queryBorrowingBasePipelineStatus, pipelineDefinitionId]);

  const loadArtifactInstances = useCallback(
    (pipelineRunId: string) => {
      // fetch artifact instances only when we are in Monitor mode.
      if (mode === 'Monitor') {
        queryArtifactInstances({
          variables: { pipelineRunId },
        });
      }
    },
    [queryArtifactInstances, mode],
  );

  // 1st time load the status.
  useEffect(() => {
    getStatus();
  }, [getStatus]);

  // mutation to save the borrowing base artifacts
  const [
    saveBorrowingBasePipelineArtifactsMutation,
    { loading: isSavingWip },
  ] = useSaveBorrowingBasePipelineArtifactDocumentWipMutation({
    onCompleted: (result) => {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: `graph saved: ${result.saveBorrowingBasePipelineArtifactDocumentWip.response}`,
        status: 'success',
      });

      getStatus();
    },
    onError: (error) => {
      onError(error, 'saveBorrowingBasePipelineArtifactsMutation.onError');
    },
    refetchQueries: [
      {
        query: GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS,
        variables: { pipelineDefinitionId },
      },
    ],
  });

  //
  const [
    resetArtifactDocumentWipToCurrent,
    { loading: isResettingToCurrent },
  ] = useDeleteBorrowingBasePipelineArtifactDocumentWipMutation({
    onCompleted: () => {
      toast({
        ...defaultToastOptions,
        id: `${Date.now()}`,
        title: `graph reset to latest committed document.`,
        status: 'success',
      });

      // refetch
      getStatus();
    },
    onError: (error) => {
      onError(error, 'resetArtifactDocumentWipToCurrent.onError');
    },
    refetchQueries: [
      {
        query: GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS,
        variables: { pipelineDefinitionId },
      },
    ],
  });

  const onResetToLatest = useCallback(() => {
    try {
      resetArtifactDocumentWipToCurrent({
        variables: {
          pipelineDefinitionId,
          artifactDocumentId: graph.artifactDocumentId,
        },
      });
    } catch (error) {
      onError(error, 'resetArtifactDocumentWipToCurrent');
    }
  }, [
    resetArtifactDocumentWipToCurrent,
    pipelineDefinitionId,
    graph.artifactDocumentId,
    onError,
  ]);

  const onGraphUploaded = useCallback(
    ({
      storedGraph,
      positions,
    }: {
      storedGraph: StoredGraphState;
      positions: GraphPositions;
    }) => {
      mergeGraph({
        ...storedGraph,
        dirty: true,
      });
      updateFlowNodes(storedGraph, positions);
    },
    [mergeGraph, updateFlowNodes],
  );

  const saveArtifactDocumentWip = useCallback(() => {
    try {
      const result = tapeBuilderGraphToArtifactsDocument({
        artifactDocumentId: graph.artifactDocumentId,
        graph,
        creditFacilities: graph.creditFacilities,
        loanProducts: graph.loanProducts,
      });

      if (result.isValid) {
        saveBorrowingBasePipelineArtifactsMutation({
          variables: {
            pipelineDefinitionId,
            artifactsDocumentString: result.artifactsDocument,
          },
        });
        saveNodePositions();
      } else {
        onError(result.errors.join(', '), 'saveArtifactDocumentWip');
      }
    } catch (error) {
      // any synchronous errors will be caught here.
      // but graphQL errors are caught in the
      // onError callback.
      onError(error, 'saveArtifactDocumentWip');
    }
  }, [
    saveBorrowingBasePipelineArtifactsMutation,
    saveNodePositions,
    graph,
    onError,
    pipelineDefinitionId,
  ]);

  const dbMessages: string[] = [];
  if (loadingStatus) {
    dbMessages.push('Loading Status...');
  }
  if (loadingArtifactInstances) {
    dbMessages.push('Loading Instances...');
  }
  if (loadingCurrentArtifactDocument) {
    dbMessages.push('Loading Artifacts...');
  }

  if (isResettingToCurrent) {
    dbMessages.push('Resetting wip back to current graph');
  }
  if (isSavingWip) {
    dbMessages.push('Saving Artifacts...');
  }

  if (savingNodePositions) {
    dbMessages.push('Saving Positions...');
  }

  const dbMessage = dbMessages.join(', ');
  const showGraph = !loadingCurrentArtifactDocument && !loadingStatus;

  const changeMode = (newMode: TapeBuilderMode) => {
    // Repoint the document at what the new mode should show; the loader effect
    // then loads it. Monitor waits for a run selection (null for now); the
    // other modes show the WIP doc.
    if (newMode === 'Monitor') {
      setCurrentArtifactDocumentId(null);
    } else {
      mergeGraph({ artifactInstances: null });
      setCurrentArtifactDocumentId(
        status?.borrowingBasePipelineStatus.artifactDocumentWipId ?? null,
      );
    }
    setMode(newMode);

    // Reflect the mode in the URL so it stays shareable. Edit is the default,
    // so keep the URL clean by dropping the param for it. The reportingDate /
    // fileVersion run params only make sense in Monitor mode.
    setSearchParams(
      (params) => {
        if (newMode === 'Edit') {
          params.delete('mode');
        } else {
          params.set('mode', newMode);
        }
        if (newMode !== 'Monitor') {
          params.delete('reportingDate');
          params.delete('fileVersion');
        }
        return params;
      },
      { replace: true },
    );
  };

  // The one place documents are loaded. currentArtifactDocumentId is the single
  // "which document to show" pointer; whenever it (or the mode's view) changes,
  // load it. Everything else in this component only *sets* that pointer:
  //   - status load  -> WIP doc (Edit/QA/View)
  //   - instance load -> selected run's committed doc (Monitor)
  //   - changeMode    -> resets it for the mode being entered
  // Monitor and View render the committed db view; Edit/QA render the WIP.
  useEffect(() => {
    if (!currentArtifactDocumentId) {
      return;
    }
    queryArtifactDocument({
      artifactDocumentId: currentArtifactDocumentId,
      dbView: mode === 'Monitor' || mode === 'View',
    });
  }, [currentArtifactDocumentId, mode, queryArtifactDocument]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    // when pipelineDefinitionId changes,
    // rerender the control with default state.
    // Skip the initial mount so a deep-linked `?mode=Monitor` is preserved
    // (this effect would otherwise clobber the URL-seeded mode back to Edit).
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setMode('Edit');
    // Also drop the Monitor run params from the URL, mirroring changeMode.
    // Otherwise a refresh/shared link would deep-link back into Monitor with
    // the previous pipeline definition's reportingDate/fileVersion.
    setSearchParams(
      (params) => {
        params.delete('mode');
        params.delete('reportingDate');
        params.delete('fileVersion');
        return params;
      },
      { replace: true },
    );
    setDiff(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineDefinitionId]);

  useEffect(() => {
    // when mode changes remove diff control.
    setDiff(null);
  }, [mode]);

  const isGraphFiltered = graph.selectedTags.length > 0;

  return (
    <GraphProvider graph={graph} dispatch={dispatch}>
      <QAProvider
        qaMode={mode === 'QA'}
        pipelineDefinitionId={pipelineDefinitionId}
      >
        <TapeBuilderStatus
          mode={mode}
          status={status}
          pipelineDefinitionId={pipelineDefinitionId}
          dirty={graph.dirty}
          dbMessage={dbMessage}
          updateStatus={getStatus}
          onSaveWip={saveArtifactDocumentWip}
          setMode={changeMode}
          diff={diff}
          setDiff={setDiff}
          onLoadArtifactInstances={loadArtifactInstances}
          setArtifactDocumentId={setCurrentArtifactDocumentId}
          onClearGraph={clearGraph}
          onResetToLatest={onResetToLatest}
          onUndoChanges={() => {
            getStatus();
          }}
          isGraphFiltered={isGraphFiltered}
        />
        {diff && <TapeBuilderDiffViewer diff={diff} />}
        {showGraph && (
          <TapeBuilderControl
            mode={mode}
            pipelineDefinitionId={pipelineDefinitionId}
            orgSlugMap={orgSlugMap}
            onGraphUploaded={onGraphUploaded}
            handleSavePositions={saveNodePositions}
          />
        )}
      </QAProvider>
    </GraphProvider>
  );
};

export const TapeBuilderForPipelineDefinition = (props: Props) => {
  return (
    <ReactFlowProvider>
      <InternalComponent {...props} />
    </ReactFlowProvider>
  );
};
